import React from "react";
import SourceMessageAccordion, { InlineSpinner } from "./shared/SourceMessageAccordion";
import { timestampFormatter } from "../../core/utils/timestampFormatter";

const TIMESTAMP_MINUTE_FORMAT = { includeSeconds: false };

/**
 * Build a concise digest preview from body text.
 *
 * @example
 * // Input
 * buildDigestPreview("Line one\nLine two\nLine three", 48);
 * // Output
 * "Line one Line two Line three"
 */
const buildDigestPreview = (digestBody, maxLength = 180) => {
  const normalized = String(digestBody || "").replace(/\s+/g, " ").trim();
  if (!normalized) {
    return "No digest content available";
  }
  return normalized.length <= maxLength
    ? normalized
    : `${normalized.slice(0, maxLength - 1).trimEnd()}...`;
};

/**
 * Parse digest body text into structured blocks for readable email rendering.
 *
 * @example
 * // Input
 * parseDigestBodyBlocks("## Summary\n- Item A\n- Item B\n\nNext steps");
 * // Output
 * [{ type: "heading", text: "Summary", level: 2 }, { type: "list", items: ["Item A", "Item B"] }, { type: "paragraph", text: "Next steps" }]
 */
const parseDigestBodyBlocks = (digestBody) => {
  const lines = String(digestBody || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let currentParagraph = [];
  let currentList = [];

  const flushParagraph = () => {
    if (!currentParagraph.length) {
      return;
    }
    blocks.push({
      type: "paragraph",
      text: currentParagraph.join(" ").trim(),
    });
    currentParagraph = [];
  };

  const flushList = () => {
    if (!currentList.length) {
      return;
    }
    blocks.push({
      type: "list",
      items: [...currentList],
    });
    currentList = [];
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushList();
      return;
    }

    const headingMatch = line.match(/^(#{1,4})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      blocks.push({
        type: "heading",
        level: headingMatch[1].length,
        text: headingMatch[2].trim(),
      });
      return;
    }

    const bulletMatch = line.match(/^[-*]\s+(.*)$/);
    if (bulletMatch) {
      flushParagraph();
      currentList.push(bulletMatch[1].trim());
      return;
    }

    const numberedMatch = line.match(/^\d+\.\s+(.*)$/);
    if (numberedMatch) {
      flushParagraph();
      currentList.push(numberedMatch[1].trim());
      return;
    }

    flushList();
    currentParagraph.push(line);
  });

  flushParagraph();
  flushList();
  return blocks;
};






/**
 * Render the workflow run history panel with digest preview and source messages.
 *
 * @example
 * // Input
 * <BusinessWorkflowRunHistory historyLoading={false} runHistory={[{ digest_date: "2026-04-28", digest_subject: "Daily digest" }]} />
 * // Output
 * History selection UI with digest preview and source message accordion.
 */
const BusinessWorkflowRunHistory = ({
  historyLoading,
  historyError,
  suppressHistoryError,
  runHistory,
  buildRunSelectionKey,
  selectedRun,
  onRunSelection,
  inputMessagesLoading,
  inputMessagesError,
  inputMessages,
  expandedMessageId,
  messageContentCache,
  onToggleSourceMessage,
}) => (
  <div className="business-run-history-wrap">
    {historyLoading ? <InlineSpinner text="Loading run history..." /> : null}
    {historyError && !suppressHistoryError ? (
      <div className="error-message">{historyError}</div>
    ) : null}
    {!historyLoading && !historyError && runHistory.length === 0 ? (
      <div className="business-helper-text">No previous digest runs found yet.</div>
    ) : null}
    {!historyLoading && !historyError && runHistory.length > 0 ? (
      <>
        <label className="form-label" htmlFor="run-history-select">
          Select run
        </label>
        <select
          id="run-history-select"
          className="form-control business-run-dropdown"
          value={selectedRun ? buildRunSelectionKey(selectedRun) : ""}
          onChange={onRunSelection}
        >
          {runHistory.map((run) => {
            const runKey = buildRunSelectionKey(run);
            const sentAt = run.created_at
              ? timestampFormatter(run.created_at, TIMESTAMP_MINUTE_FORMAT)
              : run.digest_date;
            return (
              <option key={runKey} value={runKey}>
                {`${sentAt} | ${run.digest_subject || "Untitled digest"}`}
              </option>
            );
          })}
        </select>

        <div className="business-run-split-layout">
          {selectedRun ? (
            <section className="business-run-preview-pane business-run-left-pane">
              <div className="business-run-preview-header">
                <div className="business-run-preview-subject">
                  <span className="business-run-email-badge">Email preview</span>
                  <h4>{selectedRun.digest_subject || "Untitled digest"}</h4>
                </div>
                <div className="business-run-email-meta business-run-email-meta--inline">
                  <div className="business-run-email-meta-item">
                    <span>Sent</span>
                    <strong>
                      {selectedRun.created_at
                        ? timestampFormatter(selectedRun.created_at, TIMESTAMP_MINUTE_FORMAT)
                        : selectedRun.digest_date || "-"}
                    </strong>
                  </div>
                </div>
              </div>
              {selectedRun.payload_error ? (
                <div className="error-message">
                  Could not load full payload: {selectedRun.payload_error}
                </div>
              ) : null}
              {selectedRun.digest_html ? (
                <iframe
                  title="Digest HTML preview"
                  className="business-run-preview-iframe"
                  sandbox=""
                  srcDoc={selectedRun.digest_html}
                />
              ) : (
                <div className="business-run-preview-body">
                  {parseDigestBodyBlocks(selectedRun.digest_body).length > 0 ? (
                    parseDigestBodyBlocks(selectedRun.digest_body).map((block, index) => {
                      const key = `digest-block-${index}`;
                      if (block.type === "heading") {
                        return (
                          <h5 key={key} className={`business-run-body-heading level-${block.level}`}>
                            {block.text}
                          </h5>
                        );
                      }
                      if (block.type === "list") {
                        return (
                          <ul key={key} className="business-run-body-list">
                            {block.items.map((item, itemIndex) => (
                              <li key={`${key}-item-${itemIndex}`}>{item}</li>
                            ))}
                          </ul>
                        );
                      }
                      return <p key={key}>{block.text}</p>;
                    })
                  ) : (
                    <p>{buildDigestPreview(selectedRun.digest_body)}</p>
                  )}
                </div>
              )}
            </section>
          ) : null}

          <div className="business-run-right-column">
            <div className="business-run-preview-subject">
              <span className="business-run-email-badge">Source records</span>
              <h4>Input records used for this run</h4>
            </div>
            <section className="business-run-right-pane">
            {inputMessagesLoading ? <InlineSpinner text="Loading input messages..." /> : null}
            {inputMessagesError ? <div className="error-message">{inputMessagesError}</div> : null}
            {!inputMessagesLoading && !inputMessagesError && inputMessages.length === 0 ? (
              <div className="business-helper-text">No source records found for this run window.</div>
            ) : null}
            {!inputMessagesLoading && !inputMessagesError && inputMessages.length > 0
              ? inputMessages.map((sourceMessage) => {
                  const messageId = String(sourceMessage.message_id || "");
                  return (
                    <SourceMessageAccordion
                      key={messageId}
                      sourceMessage={sourceMessage}
                      isExpanded={expandedMessageId === messageId}
                      cacheEntry={messageContentCache[messageId]}
                      onToggle={onToggleSourceMessage}
                    />
                  );
                })
              : null}
            </section>
          </div>
        </div>
      </>
    ) : null}
  </div>
);

export default BusinessWorkflowRunHistory;
